import { useEffect, useMemo, useState } from "react";
import { useGetPreviousAnswers } from "../useGetPreviousAnswers/useGetPreviousAnswers";
import { isEmpty, isUndefined } from "lodash";
const DEFAULT_REGEX = /\$\{([^{}.]+)(?:\.[^{}.]+)*\}/g;

function interpolate(
  str: string | undefined,
  variables: Record<string, unknown>
) {
  const variableNames: string[] = Object.keys(variables);
  const variableValues: unknown[] = Object.values(variables);

  return new Function(...variableNames, `return \`${str}\`;`)(
    ...variableValues
  );
}

function prepareVariables(names: string[], data: Record<string, unknown>) {
  return Array.from(new Set([...Object.keys(data), ...names])).reduce(
    (acc, key) => {
      acc[key] = isUndefined(data[key]) ? "${" + key + "}" : data[key];

      return acc;
    },
    {} as Record<string, unknown>
  );
}

const useInterpolateText = ({
  text,
}: {
  text: string | undefined;
  regex?: RegExp;
}): { interpolatedText: string; loading?: boolean; variables: Object } => {
  const variables: string[] = useMemo(
    () =>
      text ? [...text.matchAll(DEFAULT_REGEX)].map((match) => match[1]) : [],
    [text]
  );
  const variablesToFetch = useMemo(() => variables.join(","), [variables]);
  const [interpolatedText, setInterpolatedText] = useState<string>("");
  const { result, loading } = useGetPreviousAnswers(variablesToFetch);

  useEffect(() => {
    const textIsValid = isEmpty(variablesToFetch);
    const resultIsReady: boolean =
      (!isEmpty(result) || textIsValid) && !loading;
    if (textIsValid) {
      setInterpolatedText(text ?? "");
    } else if (resultIsReady) {
      setInterpolatedText(
        interpolate(text, prepareVariables(variables, result))
      );
    }
  }, [result, loading, text, variables]);
  return { interpolatedText, loading, variables: result };
};

export default useInterpolateText;
